// eslint-disable-next-line import/no-cycle
import Onboarding from '@client/models/store/onboarding';
// eslint-disable-next-line import/no-cycle
import router from '@client/router';
import {Constants, DATE_FORMAT_DB, ComplianceStatus} from '@client/util/constants';
import Goal from '@client/models/store/goal';
import Custodian from '@client/util/custodian';
// eslint-disable-next-line import/no-cycle
import accountRequestService from '@client/services/accountRequest';
import {Logger, Category} from '@simpleinvest/front-end-logger';
// eslint-disable-next-line import/no-cycle
import ssoService from '@client/services/sso';
// eslint-disable-next-line import/no-cycle
import OnboardingService from '@client/services/onboarding';
import {ErrorRouteNames} from '@client/routes/errors';
import {displayErrorBanner} from '@client/events/notification-bus';
// eslint-disable-next-line import/no-cycle
import settings from '@client/services/settings';
import {format, addYears, endOfDay} from 'date-fns';
import {formatDateToDb} from '@client/util/format';
import {onboardingGoingLiveBeforeServicing} from '@client/remote-config';

const onboardingLiveBeforeServicing = async () => {
  const onboardingBeforeServicing = await onboardingGoingLiveBeforeServicing();

  return onboardingBeforeServicing;
};

const logger = new Logger({
  category: Category.ClientOnboarding,
  fileName: 'onboarding.js',
});

const stateObj = () => new Onboarding();

const gettersObj = {
  isOnboardingActive: ({active}) => active,
  isJsonValid: (state) =>
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    !!(state.thirdPartyData && state.thirdPartyData.PFG_Wrapper && state.thirdPartyData.PFG_Wrapper.Provider_Info),
  // eslint-disable-next-line consistent-return
  getSalary: (state) => {
    if (state.salary) {
      return state.salary;
    }
  },

  // eslint-disable-next-line consistent-return
  overrideAnnualIncomeId: (state, getters, rootState) => {
    if (rootState.account.requests.incomeIntervals.length > 0) {
      const annualIncomeObj = rootState.account.requests.incomeIntervals.find((element) => {
        const minIncomeValue = Number.parseInt(element.minIncome.replace(/\D/gi, ''), 10) || 0;
        const maxIncomeValue = Number.parseInt(element.maxIncome.replace(/\D/gi, ''), 10) || state.salary;

        return state.salary >= minIncomeValue && state.salary <= maxIncomeValue;
      });

      // eslint-disable-next-line no-undefined
      if (annualIncomeObj === undefined) {
        return [...rootState.account.requests.incomeIntervals].pop().id;
      }

      return annualIncomeObj.id;
    }
  },

  // eslint-disable-next-line consistent-return
  conventionalNetWorth: (state, getters, rootState) => {
    if (rootState.account.requests.netWorthIntervals.length > 0) {
      const netWorthObj = rootState.account.requests.netWorthIntervals.find((element) => {
        const minNetWorthValue = Number.parseInt(element.minNetWorth.replace(/\D/gi, ''), 10) || 0;
        const maxNetWorthValue = element.maxNetWorth
          ? Number.parseInt(element.maxNetWorth.replace(/\D/gi, ''), 10)
          : state.amountToInvest;

        return state.amountToInvest >= minNetWorthValue && state.amountToInvest <= maxNetWorthValue;
      });

      return netWorthObj.id;
    }
  },

  // eslint-disable-next-line consistent-return
  netWorthRange: (state, getters, rootState) => {
    if (state.thirdPartyData) {
      let preTaxBalance = 0;
      let postTaxBalance = 0;

      if (state.thirdPartyData.PFG_Wrapper.Provider_Info['Pre-tax_Balance'].Balance) {
        preTaxBalance = Number.parseInt(state.thirdPartyData.PFG_Wrapper.Provider_Info['Pre-tax_Balance'].Balance, 10);
      }

      if (state.thirdPartyData.PFG_Wrapper.Provider_Info['Post-tax_Balance'].Balance) {
        postTaxBalance = Number.parseInt(state.thirdPartyData.PFG_Wrapper.Provider_Info['Post-tax_Balance'].Balance, 10);
      }

      const pfgTaxBalance = preTaxBalance + postTaxBalance;

      if (rootState.account.requests.netWorthIntervals.length > 0 && pfgTaxBalance) {
        const netWorthObj = rootState.account.requests.netWorthIntervals.find(
          (element) =>
            pfgTaxBalance >= Number.parseInt(element.minNetWorth.replace(/\D/gi, ''), 10) &&
            pfgTaxBalance <= Number.parseInt(element.maxNetWorth.replace(/\D/gi, ''), 10)
        );

        return netWorthObj.id;
      }

      return 1;
    }
  },
  isFinalStep: (state) => {
    const currentStepIndex = state.STEPS.findIndex((step) => step.id === state.currentStep);

    return currentStepIndex === state.STEPS.length - 1;
  },
  editable: (state) => state.editable,
  getPortfolioType: (state) => state.portfolioType,
  isADOInitiated: (state) => state.complianceStatus,
};

const actions = {
  // ONE DAY IN THE FUTURE: peek/fetch/find
  async findOnboarding({dispatch, state}) {
    // we explicitly set it to null when initializing, whenever we set it, we coerce to bool, soooo...
    if (state.active !== null) return;

    await dispatch('fetchOnboarding');
  },

  async getCustodian({commit, rootState, dispatch}, requestId) {
    // eslint-disable-next-line unicorn/no-fn-reference-in-iterator, unicorn/prefer-array-some, unicorn/no-array-callback-reference
    const response = await accountRequestService.find(requestId);

    const custodianId = new Custodian(rootState).getIdByName(response.custodian);

    commit('SET_CUSTODIAN_ID', custodianId);
    commit('user/SET_CUSTODIAN_ID', custodianId, {root: true});
    commit('user/SET_CUSTODIAN_NAME', response.custodian, {root: true});
    dispatch('account/requests/findCatAccountHolderType', response, {root: true});
  },
  setOnboardingData({commit}, payload) {
    commit('SET_ONBOARDING_DATA', payload);
  },
  async fetchOnboarding({state, dispatch}) {
    if (!state.active) await dispatch('user/getClientInformation', null, {root: true});
  },
  async getOnboarding({commit, rootState, dispatch, state}) {
    await Promise.all([
      // dependencies for setting onboarding status
      dispatch('risk/fetchRtqData', null, {root: true}),
      dispatch('goal/getGoalSelection', null, {root: true}),
    ]);

    await dispatch('fetchOnboarding');
    if (state.active === null) return;

    // select our goal based on the data we saved
    if (state.goalId !== null) {
      const toAdd = rootState.goal.selectOptions.find((goal) => goal.advisorGoalId === state.goalId);

      if (toAdd) {
        await Promise.all([
          dispatch('goal/setToAdd', toAdd, {root: true}),
          dispatch('goal/setToAddTarget', state.goalDateInfo, {root: true}),
          dispatch('goal/updateOption', toAdd, {root: true}),
        ]);
      } else {
        // in this situation the user has selected a goal
        // then came back later and the goal is no longer an option
        logger.addBreadcrumb({
          callingFuncName: 'getOnboarding',
          message: `Goal ID was not found`,
        });
        logger.logException({error: 'Goal ID was not found'});
      }
    }

    // figure out our current step based on what data we have
    // For new onboarding flow redirect to smart IRA landing page
    if ((await onboardingLiveBeforeServicing()) && state.complianceStatus === null) {
      window.location.href =
        process.env.VUE_APP_ENV === 'prod'
          ? 'https://secure05.principal.com/participant/member/options-smartira'
          : 'https://secure05.pilot.principal.com/participant/member/options-smartira';
    } else {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      await findCurrentStep(state, rootState, commit);
    }

    if (state.allocationSet && state.type.toUpperCase() === 'CONVENTIONAL') {
      commit('SET_CURRENT_STEP', 'open-account');
    }

    // eslint-disable-next-line unicorn/prefer-includes
    if (['asset-allocation', 'contact-information', 'confirmation'].includes(state.currentStep) && state.goalId === null) {
      commit('SET_GOAL_DATA', {
        goalId: null,
        goalDateInfo: null,
      });
      commit('SET_ALLOCATION', false);

      // save it
      dispatch('saveOnboarding');
    }
  },

  setOnboardingType({commit, rootGetters}) {
    commit('SET_TYPE', rootGetters.onboardingType);
  },
  // eslint-disable-next-line require-await
  async setSettings({commit}, payload) {
    commit('SET_SETTINGS', payload);
  },
  setRequestsSent({commit}, payload) {
    commit('SET_REQUESTS_SENT', payload);
  },
  // eslint-disable-next-line require-await
  async setActive({commit}, payload) {
    commit('SET_ACTIVE', payload);
  },
  // eslint-disable-next-line require-await
  async setSalary({commit}, payload) {
    let localPayload = payload;

    // eslint-disable-next-line no-restricted-globals
    if (!localPayload || Number.isNaN(localPayload)) {
      localPayload = 0;
    }

    commit('SET_SALARY', localPayload);
  },
  // eslint-disable-next-line require-await
  async setAllocation({commit}, payload) {
    commit('SET_ALLOCATION', payload);
  },
  // eslint-disable-next-line require-await
  async setCurrentStep({commit, dispatch}, payload) {
    commit('SET_CURRENT_STEP', payload.step);
    if (payload.step === 'asset-allocation') {
      await dispatch('getOnboardingData');
      commit('SET_ALLOCATION', false);
      dispatch('saveOnboarding', {portfolioType: payload.portfolioType});
    }

    if (payload.step === 'risk-tolerance') {
      // we need to set so we know what step we are on
      commit('SET_ALLOCATION', false);
      dispatch('saveOnboardingPortfolio', payload.portfolioType);
    }
  },
  saveOnboardingPortfolio({dispatch}, portfolioType) {
    if (portfolioType) {
      dispatch('saveOnboarding', {portfolioType: null, updatePortfolioTypeToNull: true});
    } else {
      dispatch('saveOnboarding');
    }
  },
  async saveOnboarding({commit, rootState, rootGetters, state}, reqPayload) {
    let goalDateInfo = null;
    let goalId = null;

    if (rootState.goal.toAdd) {
      goalId = rootState.goal.toAdd.advisorGoalId;

      if (rootState.goal.toAdd.targetDate === null && rootState.goal.toAdd.typeId === 'Model Portfolio') {
        // eslint-disable-next-line no-param-reassign
        rootState.goal.toAdd.targetDate = format(addYears(endOfDay(new Date()), 20), DATE_FORMAT_DB);
      }

      if (rootState.goal.toAdd.targetDate === null || rootState.goal.toAdd.targetDate === 'invalid date') {
        // eslint-disable-next-line no-param-reassign
        rootState.goal.toAdd.targetDate = Constants.DEFAULT_TARGET_DATE;
      }

      goalDateInfo = formatDateToDb(rootState.goal.toAdd.targetDate);
      if (Goal.isAgeType(rootState.goal.toAdd)) {
        goalDateInfo = rootState.goal.toAdd.retirementAge;
      }

      commit('SET_GOAL_DATA', {
        goalId,
        goalDateInfo,
      });
    }

    let payload = {
      income: state.salary,
      invest: state.amountToInvest,
      goalId,
      goalDateInfo,
      allocationSet: state.allocationSet,
      requestsSent: state.requestsSent,
      active: state.active,
      clUserId: rootGetters['cognitoUser/userId'],
    };

    if (reqPayload) {
      payload = {...payload, ...reqPayload};
    }

    const adoPayload = {
      complianceStatus: 'AWAITING_COMPLIANCE_APPROVAL',
    };

    if (state.complianceStatus === ComplianceStatus.ClientUnapproved) {
      payload = {...payload, ...adoPayload};
    }

    const data = await OnboardingService.saveOnboarding(payload);

    commit('SET_PORTFOLIO_TYPE', data.portfolioType);
  },

  // eslint-disable-next-line require-await
  async gotoNextStep({commit, state, getters}) {
    const currentStepIndex = state.STEPS.findIndex((step) => step.id === state.currentStep);

    if (getters.isFinalStep) {
      // do a final save?
    } else {
      // go to the next step
      commit('SET_CURRENT_STEP', state.STEPS[currentStepIndex + 1].id);
    }
  },
  saveInvestmentProfile({rootState, getters, dispatch, state}) {
    return new Promise((resolve) => {
      let payload = rootState.user.investmentProfile;
      const netWorthId = state.type.toUpperCase() === 'CONVENTIONAL' ? getters.conventionalNetWorth : getters.netWorthRange;
      const data = {
        annual_income_id: getters.overrideAnnualIncomeId,
        liquid_net_worth_id: netWorthId,
        total_net_worth_id: netWorthId,
      };

      // we just want to save our default data
      payload = payload === null ? Constants.DEFAULT_INVESTMENT_PROFILE : payload;
      dispatch('user/saveInvestmentProfile', {...payload, ...data}, {root: true});
      resolve();
    });
  },
  async getThirdPartyData({commit, rootGetters}, payload) {
    const enterpriseId = rootGetters['cognitoUser/enterpriseId'];
    const eid = rootGetters['cognitoUser/preferredUsername'];
    const onboardingData = await ssoService.getOnboardingData({
      thirdPartyPrimaryId: eid,
      enterpriseId,
      getUserData: true,
    });
    let onboardingResponse;

    try {
      onboardingResponse = JSON.parse(onboardingData?.userData);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getThirdPartyData',
        message: error.message,
      });
    }

    if (payload.onboardingType !== 'Conventional' && !onboardingResponse?.PFG_Wrapper) {
      displayErrorBanner('There was an error accessing your information. Please try again later or contact us.');

      logger.logException({error: new Error('Onboarding type is SSO. Thirdparty data is missing.')});

      router.push({name: ErrorRouteNames.SystemErrorPage});

      return;
    }

    commit('SET_THIRDPARTY_DATA', onboardingResponse);
  },
  async updateThirdPartyData({dispatch, getters, rootState, state}) {
    try {
      await state.updateThirdpartyData({dispatch, getters, rootState, state});
    } catch (error) {
      logger.logException({error});

      router.push({name: ErrorRouteNames.SystemErrorPage});
    }
  },
  prePopulateMaritalStatus({dispatch, rootState, state}) {
    state.prepopulateMaritalStatus(state, dispatch, rootState);
  },
  // eslint-disable-next-line require-await
  async setInvestmentAmount({commit}, payload) {
    // eslint-disable-next-line no-restricted-globals
    if (Number.isNaN(payload)) {
      // eslint-disable-next-line no-param-reassign
      payload = null;
    }

    commit('SET_INVESTMENT_AMOUNT', payload);
  },

  async checkParams({rootState}, {cdoParameter}) {
    try {
      const {riaId} = rootState;
      const result = await settings.getOnboardingStatus(riaId, cdoParameter);
      const enabled = result.data?.data?.onboardingEnabled || false;

      return enabled;
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'checkParams',
        message: `getOnboardingStatus error caught`,
      });
      logger.logException({error});

      return false;
    }
  },
  async getOnboardingData({rootGetters, commit}) {
    const payload = {clUserId: rootGetters['cognitoUser/userId']};
    const data = await OnboardingService.getOnboarding(payload);

    commit('SET_PORTFOLIO_TYPE', data.portfolioType);

    return data;
  },
};

const mutations = {
  SET_CUSTODIAN_ID(state, id) {
    state.custodianId = id;
  },
  SET_TYPE(state, type) {
    state.type = type;
    //  update step4 based on cdo type
    if (type.toUpperCase() === 'CONVENTIONAL') {
      // eslint-disable-next-line no-param-reassign
      state.STEPS[4] = {
        name: 'Open account',
        id: 'open-account',
      };
    }
  },
  SET_SALARY(state, salary) {
    state.salary = salary;
  },
  SET_CURRENT_STEP(state, payload) {
    state.currentStep = payload;
  },
  SET_ONBOARDING(state, payload) {
    state.salary = payload.income;
    state.amountToInvest = payload.invest;
    state.goalId = payload.goal_id;
    state.goalDateInfo = payload.goal_date_info;
    state.allocationSet = !!payload.allocation_set;
    state.requestsSent = !!payload.requests_sent;
    state.active = !!payload.active;
    state.isOnboarding = state.active;
  },
  SET_ONBOARDING_DATA(state, payload) {
    state.salary = payload.income;
    state.amountToInvest = payload.invest;
    state.goalId = payload.goalId;
    state.goalDateInfo = payload.goalDateInfo;
    state.allocationSet = !!payload.allocationSet;
    state.requestsSent = !!payload.requestSent;
    state.active = !!payload.active;
    state.isOnboarding = state.active;
    state.complianceStatus = payload.complianceStatus;
  },
  SET_PORTFOLIO_TYPE(state, payload) {
    state.portfolioType = payload;
  },
  SET_ALLOCATION(state, payload) {
    state.allocationSet = payload;
  },
  SET_GOAL_DATA(state, payload) {
    state.goalId = payload.goalId;
    state.goalDateInfo = payload.goalDateInfo;
  },
  SET_THIRDPARTY_DATA(state, payload) {
    state.thirdPartyData = payload;
    state.editable.loadData(payload);
  },
  SET_REQUESTS_SENT(state, payload) {
    state.requestsSent = payload;
  },
  SET_ACTIVE(state, payload) {
    state.active = payload;
  },
  SET_SETTINGS(state, payload) {
    state.settings = payload;
  },
  SET_AGE(state, payload) {
    state.age = payload;
  },
  SET_INVESTMENT_AMOUNT(state, payload) {
    state.amountToInvest = payload;
  },
  SET_IS_ONBOARDING(state, payload) {
    state.isOnboarding = payload;
  },
};

// eslint-disable-next-line func-style
async function findCurrentStep(state, rootState, commit) {
  if (state.complianceStatus === ComplianceStatus.Rejected) {
    router.push({name: ErrorRouteNames.NotFoundPageADO});
    throw new Error('COMPLIANCE REJECTED');
  }

  if (
    state.complianceStatus === ComplianceStatus.ClientUnapproved ||
    state.complianceStatus === ComplianceStatus.ClientApproved
  ) {
    if (await onboardingLiveBeforeServicing()) {
      window.location.href =
        process.env.VUE_APP_ENV === 'prod'
          ? 'https://iraenroll.individuals.principal.com/final-review'
          : 'https://iraenroll.individuals.staging.principal.com/final-review';
    }

    commit('SET_CURRENT_STEP', 'confirmation');

    return;
  }

  if (state.complianceStatus !== null) return;

  if (state.allocationSet) {
    commit('SET_CURRENT_STEP', 'contact-information');
  } else if (rootState.risk.isComplete && state.goalId !== null) {
    commit('SET_CURRENT_STEP', 'asset-allocation');
  } else if (state.goalId !== null) {
    commit('SET_CURRENT_STEP', 'risk-tolerance');
  } else if (state.salary) {
    commit('SET_CURRENT_STEP', 'goal-select');
  } else {
    commit('SET_CURRENT_STEP', 'get-started');
  }
}

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
};
